import React, { useState } from 'react';
import { ref, set, getDatabase } from 'firebase/database';
import { app } from '../../utils/firebase';
import { fetchChannelsFromGitHub } from '../../utils/fetchChannelsFromGitHub.mjs';
import { CHANNELS_CACHE_PATH } from '../../utils/fetchChannelsFromFirebaseCache';
import { useChannelData } from '../../context/ChannelDataContext';
import { useLanguage } from '../../context/LanguageContext';

const db = getDatabase(app);

/**
 * Writes the same snapshot as `npm run sync-channels`.
 * RTDB: see database.rules.json (channels_cache.write allows admins via admins/{uid} or profiles isAdmin).
 */
export default function AdminChannelSync() {
  const { refresh } = useChannelData();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);
  const [status, setStatus] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setStatus(t('Téléchargement depuis GitHub (cela peut prendre une minute)...', 'Downloading from GitHub (this can take a minute)...'));
    try {
      const data = await fetchChannelsFromGitHub();
      setStatus(t('Écriture dans Firebase...', 'Writing to Firebase...'));
      const payload = {
        countries: data.countries,
        categories: data.categories,
        countriesMetadata: data.countriesMetadata,
        updatedAt: Date.now(),
      };
      await set(ref(db, CHANNELS_CACHE_PATH), payload);
      setStatus(t("Synchronisation terminée. Les données de l'application ont été actualisées.", 'Sync complete. Live app data refreshed.'));
      refresh();
    } catch (e) {
      setError(e?.message || String(e));
      setStatus('');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t('Données des chaînes', 'Channel data')}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          {t("Récupère le dernier jeu de données depuis famelack/famelack-data et l'enregistre dans Realtime Database à ", 'Pulls the latest dataset from famelack/famelack-data and saves it to Realtime Database at ')}
          <code className="rounded bg-slate-900 px-1.5 py-0.5 font-mono text-xs text-brand-300">/{CHANNELS_CACHE_PATH}</code>. {t('Équivaut à la commande CLI', 'Matches the CLI command')}{' '}
          <code className="rounded bg-slate-900 px-1.5 py-0.5 font-mono text-xs text-brand-300">npm run sync-channels</code>. {t('Assurez-vous que les règles RTDB autorisent les écritures admin sur ce chemin.', 'Ensure RTDB rules allow admin writes on that path.')}
        </p>
      </div>

      <div className="ui-panel p-6">
        <button type="button" onClick={handleSync} disabled={syncing} className="ui-btn-primary">
          {syncing ? t('Synchronisation…', 'Syncing...') : t('Synchroniser les chaînes depuis GitHub', 'Sync channels from GitHub')}
        </button>
        {status ? <p className="mt-4 text-sm text-emerald-400/90">{status}</p> : null}
        {error ? <p className="mt-4 whitespace-pre-wrap text-sm text-rose-300">{error}</p> : null}
      </div>
    </div>
  );
}
